import {
  Button,
  Collapse,
  Divider,
  FormControlLabel,
  Grid,
  Paper,
  Switch,
  TextField,
  Typography
} from '@material-ui/core';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import { UpdateRounded } from '@material-ui/icons';
import { Autocomplete } from '@material-ui/lab';
import { useTranslation } from 'react-i18next';
import { unwrapResult } from '@reduxjs/toolkit';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { UpdateProjectDocument } from '../../api/project-documents';
import { RootState } from '../../app/store';
import ProjectInfoCard from '../../components/ProjectInfoCard';
import { useAppDispatch } from '../../hooks/useAppRudux';
import useMasterDateForDocument from '../../hooks/useMasterDateForDocument';
import Breadcrumb from '../Breadcrumb';
import loadingSlice from '../Loading/loadingSlice';
import {
  getProjectDocumentAsync,
  updateProjectDocumentAsync
} from './projectDocumentSlice';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      margin: theme.spacing(1.5, 0, 12, 0)
    },
    title: {
      margin: theme.spacing(1.5, 0, 2, 0),
      [theme.breakpoints.down('xs')]: {
        textAlign: 'center',
        margin: theme.spacing(3.5, 0, 4, 0)
      }
    },
    card: {
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2),
      borderRadius: theme.spacing(2)
    },
    additionalNoCard: {
      padding: theme.spacing(1, 2, 1.5, 2),
      borderRadius: theme.spacing(1)
    },
    additionalNoField: {
      '& > *': {
        marginBottom: theme.spacing(1.5),
        '&:last-child': {
          marginBottom: theme.spacing(0)
        }
      }
    },
    divider: {
      margin: theme.spacing(0.5, 0, 2, 0),
      width: theme.spacing(4)
    },
    fullWidth: {
      width: '100%'
    },
    textAlignRight: {
      textAlign: 'right'
    },
    marginBottom: {
      marginBottom: theme.spacing(2)
    },
    showInformation: {
      [theme.breakpoints.down('sm')]: {
        display: 'none'
      }
    }
  })
);

export default function DocumentEdit() {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const params = useParams<{ docId: string }>();
  const docId = Number.parseInt(params.docId, 10);

  const { data: accessControls } = useSelector(
    (state: RootState) => state.accessControls
  );
  const { selectedProjectDocument } = useSelector(
    (state: RootState) => state.projectDocuments
  );
  const { selectedProject } = useSelector((state: RootState) => state.projects);

  const [contractors, disciplines, documents, users] =
    useMasterDateForDocument();

  const [docUpdate, setDocUpdate] = useState<UpdateProjectDocument>({
    id: null,
    projectId: null,
    discipline: null,
    document: null,
    no: '',
    name: '',
    consultant: null,
    contractor: null,
    subContractor: null,
    clientDocumentNo: null,
    epcDocumentNo: null,
    vendorDocumentNo: null,
    accessControl: null,
    documentCreator: null,
    isAIMS: false,
    aimsDocumentNo: null
  });
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    const fetchCurrentDocument = () => {
      if (selectedProjectDocument) {
        setDocUpdate({
          ...selectedProjectDocument,
          consultant:
            contractors.find(
              (contractor) =>
                contractor.id === selectedProjectDocument.consultantId
            ) ?? null,
          contractor:
            contractors.find(
              (contractor) =>
                contractor.id === selectedProjectDocument.contractorId
            ) ?? null,
          subContractor:
            contractors.find(
              (contractor) =>
                contractor.id === selectedProjectDocument.subContractorId
            ) ?? null,
          discipline:
            disciplines.find(
              (discipline) =>
                discipline.id === selectedProjectDocument.disciplineId
            ) ?? null,
          document:
            documents.find(
              (document) => document.id === selectedProjectDocument.documentId
            ) ?? null,
          documentCreator:
            users.find(
              (user) => user.id === selectedProjectDocument.documentCreatorId
            ) ?? null,
          accessControl:
            accessControls.find(
              (accessControl) =>
                accessControl.id === selectedProjectDocument.accessControlId
            ) ?? null
        });
      }
    };
    if (disciplines.length > 0 && documents.length > 0 && users.length > 0) {
      fetchCurrentDocument();
    }
  }, [selectedProjectDocument, disciplines, documents, users]);

  useEffect(() => {
    dispatch(getProjectDocumentAsync(docId));
  }, []);

  async function onSubmitDocument(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    try {
      setLoading(true);
      const result = await dispatch(updateProjectDocumentAsync(docUpdate));
      await unwrapResult(result);
      dispatch(
        loadingSlice.actions.success({
          message: t('messages.success.documentUpdated') as string
        })
      );
      setTimeout(() => {
        history.push(`/documents/${docId}`);
      }, 1000);
    } catch (error) {
      setLoading(false);
    }
  }

  return (
    <div className={classes.root}>
      <Breadcrumb
        name="editDocument"
        data={{
          projectName: selectedProject?.name,
          projectId: selectedProject?.id,
          documentName: selectedProjectDocument?.name,
          documentId: selectedProjectDocument?.id
        }}
      />
      <Typography className={classes.title} component="p" variant="h4">
        {t('projectDocument.editDocumentTitle')}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={3} className={classes.showInformation}>
          {selectedProject && <ProjectInfoCard project={selectedProject} />}
        </Grid>
        <Grid item xs={12} md={9}>
          <form onSubmit={onSubmitDocument}>
            <Paper className={classes.card}>
              <Typography variant="subtitle1" gutterBottom>
                {t('projectDocument.documentInformation')}
              </Typography>
              <Divider className={classes.divider} />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={5}>
                  <Autocomplete
                    className={classes.fullWidth}
                    id="discipline"
                    options={disciplines}
                    getOptionLabel={(option) => option?.name}
                    value={docUpdate.discipline}
                    getOptionSelected={(option, value) =>
                      option.id === value.id
                    }
                    onChange={(event, newValue) => {
                      setDocUpdate({
                        ...docUpdate,
                        discipline: newValue,
                        document: null,
                        no: '',
                        name: '',
                        clientDocumentNo: null,
                        epcDocumentNo: null,
                        vendorDocumentNo: null
                      });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        size="small"
                        label={t('projectDocument.discipline')}
                        required
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={7}>
                  <Autocomplete
                    className={classes.fullWidth}
                    id="document-template"
                    options={documents
                      ?.filter(
                        (document) =>
                          !docUpdate.discipline ||
                          document.disciplineId === docUpdate.discipline?.id
                      )
                      .sort((a, b) => -b.name.localeCompare(a.name))}
                    getOptionLabel={(option) => option?.name}
                    value={docUpdate.document}
                    getOptionSelected={(option, value) =>
                      option.id === value.id
                    }
                    onChange={(event, newValue) => {
                      const discipline = disciplines.find(
                        (discipline) => discipline.id === newValue?.disciplineId
                      );
                      setDocUpdate({
                        ...docUpdate,
                        discipline: discipline
                          ? discipline
                          : docUpdate.discipline,
                        document: newValue,
                        no: newValue ? newValue?.no : '',
                        name: newValue ? newValue?.name : '',
                        clientDocumentNo: null,
                        epcDocumentNo: null,
                        vendorDocumentNo: null
                      });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        size="small"
                        label={t('projectDocument.documentTemplate')}
                        required
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={5}>
                  <TextField
                    className={classes.fullWidth}
                    id="document-no"
                    variant="outlined"
                    size="small"
                    label={t('projectDocument.documentNo')}
                    disabled={!docUpdate.document}
                    value={docUpdate.no}
                    onChange={(event) => {
                      setDocUpdate({
                        ...docUpdate,
                        no: event.target.value
                      });
                    }}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={7}>
                  <TextField
                    className={classes.fullWidth}
                    id="document-name"
                    variant="outlined"
                    size="small"
                    label={t('projectDocument.documentName')}
                    disabled={!docUpdate.document}
                    value={docUpdate.name}
                    onChange={(event) => {
                      setDocUpdate({
                        ...docUpdate,
                        name: event.target.value
                      });
                    }}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <Autocomplete
                    className={classes.fullWidth}
                    id="consultant"
                    options={contractors}
                    getOptionLabel={(option) => option?.name}
                    value={docUpdate.consultant}
                    getOptionSelected={(option, value) =>
                      option.id === value.id
                    }
                    onChange={(event, newValue) => {
                      setDocUpdate({
                        ...docUpdate,
                        consultant: newValue
                      });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        size="small"
                        label={t('projectDocument.consultant')}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Autocomplete
                    className={classes.fullWidth}
                    id="contractor"
                    options={contractors}
                    getOptionLabel={(option) => option?.name}
                    value={docUpdate.contractor}
                    getOptionSelected={(option, value) =>
                      option.id === value.id
                    }
                    onChange={(event, newValue) => {
                      setDocUpdate({
                        ...docUpdate,
                        contractor: newValue
                      });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        size="small"
                        label={t('projectDocument.mainContractor')}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Autocomplete
                    className={classes.fullWidth}
                    id="sub-contractor"
                    options={contractors}
                    getOptionLabel={(option) => option?.name}
                    value={docUpdate.subContractor}
                    getOptionSelected={(option, value) =>
                      option.id === value.id
                    }
                    onChange={(event, newValue) => {
                      setDocUpdate({
                        ...docUpdate,
                        subContractor: newValue
                      });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        size="small"
                        label={t('projectDocument.subContractor')}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={5}>
                  <Autocomplete
                    className={classes.fullWidth}
                    id="access-control"
                    options={accessControls}
                    getOptionLabel={(option) => option?.name}
                    value={
                      docUpdate.accessControl ? docUpdate.accessControl : null
                    }
                    getOptionSelected={(option, value) =>
                      option.id === value.id
                    }
                    onChange={(event, newValue) => {
                      setDocUpdate({
                        ...docUpdate,
                        accessControl: newValue
                      });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        size="small"
                        label={t('projectDocument.documentLevel')}
                        required
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={7}>
                  <Autocomplete
                    className={classes.fullWidth}
                    id="document-creator"
                    options={users}
                    getOptionLabel={(option) =>
                      `${option?.firstName} ${option?.lastName}`
                    }
                    value={docUpdate.documentCreator}
                    getOptionSelected={(option, value) =>
                      option.id === value.id
                    }
                    onChange={(event, newValue) => {
                      setDocUpdate({
                        ...docUpdate,
                        documentCreator: newValue
                      });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        size="small"
                        label={t('projectDocument.documentCreator')}
                        required
                      />
                    )}
                  />
                </Grid>
                <Grid className={classes.textAlignRight} item xs={12} sm={5}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={docUpdate.isAIMS}
                        onChange={(event) => {
                          setDocUpdate({
                            ...docUpdate,
                            isAIMS: event.target.checked,
                            aimsDocumentNo: null
                          });
                        }}
                        color="primary"
                      />
                    }
                    label={t('projectDocument.requestFromAIMS')}
                    labelPlacement="start"
                  />
                </Grid>
                <Grid
                  component={Collapse}
                  in={docUpdate.isAIMS}
                  item
                  xs={12}
                  sm={7}
                >
                  <TextField
                    className={classes.fullWidth}
                    error={
                      docUpdate.aimsDocumentNo !== null &&
                      docUpdate.aimsDocumentNo.substring(0, 3) !== 'DAR'
                    }
                    helperText={
                      docUpdate.aimsDocumentNo &&
                      docUpdate.aimsDocumentNo.substring(0, 3) !== 'DAR'
                        ? t('projectDocument.aimsDocumentNoError')
                        : ''
                    }
                    id="aims-document-no"
                    variant="outlined"
                    size="small"
                    label={t('projectDocument.aimsDocumentNo')}
                    value={
                      docUpdate.aimsDocumentNo ? docUpdate.aimsDocumentNo : ''
                    }
                    onChange={(event) => {
                      setDocUpdate({
                        ...docUpdate,
                        aimsDocumentNo:
                          event.target.value === '' ? null : event.target.value
                      });
                    }}
                    required={docUpdate.isAIMS}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Paper
                    className={classes.additionalNoCard}
                    variant="outlined"
                  >
                    <Typography variant="body2" color="textSecondary">
                      {t('projectDocument.additionalInformation')}
                    </Typography>
                    <Divider className={classes.divider} />
                    <div className={classes.additionalNoField}>
                      <TextField
                        className={classes.fullWidth}
                        id="client-document-no"
                        variant="outlined"
                        size="small"
                        label={t('projectDocument.clientDocumentNo')}
                        disabled={!docUpdate.document}
                        value={
                          docUpdate.clientDocumentNo
                            ? docUpdate.clientDocumentNo
                            : ''
                        }
                        onChange={(event) => {
                          setDocUpdate({
                            ...docUpdate,
                            clientDocumentNo:
                              event.target.value === ''
                                ? null
                                : event.target.value
                          });
                        }}
                      />
                      <TextField
                        className={classes.fullWidth}
                        id="epc-document-no"
                        variant="outlined"
                        size="small"
                        label={t('projectDocument.epcDocumentNo')}
                        disabled={!docUpdate.document}
                        value={
                          docUpdate.epcDocumentNo ? docUpdate.epcDocumentNo : ''
                        }
                        onChange={(event) => {
                          setDocUpdate({
                            ...docUpdate,
                            epcDocumentNo:
                              event.target.value === ''
                                ? null
                                : event.target.value
                          });
                        }}
                      />
                      <TextField
                        className={classes.fullWidth}
                        id="vendor-document-no"
                        variant="outlined"
                        size="small"
                        label={t('projectDocument.vendorDocumentNo')}
                        disabled={!docUpdate.document}
                        value={
                          docUpdate.vendorDocumentNo
                            ? docUpdate.vendorDocumentNo
                            : ''
                        }
                        onChange={(event) => {
                          setDocUpdate({
                            ...docUpdate,
                            vendorDocumentNo:
                              event.target.value === ''
                                ? null
                                : event.target.value
                          });
                        }}
                      />
                    </div>
                  </Paper>
                </Grid>
              </Grid>
            </Paper>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              fullWidth
              disabled={loading}
              startIcon={<UpdateRounded />}
            >
              {t('projectDocument.updateDocument')}
            </Button>
          </form>
        </Grid>
      </Grid>
    </div>
  );
}
